/**
 * Pressure Regulator — maintains downstream pressure at setpoint.
 *
 * Self-regulating: adjusts internal flow resistance to maintain P_out = P_set.
 * Essentially a control valve with built-in PID on downstream pressure.
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';

export class NSPressureRegulator implements NSComponent {
  id: string;
  type = 'pressureRegulator';
  params: Record<string, any>;
  private integral = 0;
  private prevError = 0;

  constructor(id: string, params: {
    P_set?: number;         // Pa — target downstream pressure (default 500 kPa)
    Kp?: number;            // Proportional gain (default 0.001)
    Ki?: number;            // Integral gain (default 0.0001)
    Kd?: number;            // Derivative gain (default 0)
    maxFlow?: number;       // kg/s — maximum through-flow (default 100)
  }) {
    this.id = id;
    // Gains are dimensionless (error is normalized by P_set)
    this.params = { P_set: 500000, Kp: 0.5, Ki: 0.1, Kd: 0, maxFlow: 100, ...params };
  }

  applyBoundaryConditions(inlet: Junction, outlet: Junction, dt: number): ComponentUpdate {
    const { P_set, Kp, Ki, Kd, maxFlow } = this.params;

    // PID on normalized pressure error (error/P_set → dimensionless)
    // This ensures gains are independent of absolute pressure level.
    const error_norm = P_set > 0 ? (P_set - outlet.P) / P_set : 0;
    this.integral += error_norm * dt;
    this.integral = Math.max(-10, Math.min(10, this.integral)); // Anti-windup (dimensionless)
    const derivative = dt > 0 ? (error_norm - this.prevError) / dt : 0;
    this.prevError = error_norm;

    // Output is dimensionless; scale by P_set to get pressure adjustment
    const output = (Kp * error_norm + Ki * this.integral + Kd * derivative) * P_set;
    const deltaP = Math.max(-inlet.P * 0.9, Math.min(inlet.P, output));

    return {
      deltaP,
      deltaH: 0,
      momentumSource: 0,
      energySource: 0,
      metadata: { P_set, error: error_norm, deltaP, opening: Math.max(0, Math.min(1, 0.5 + output / inlet.P)) },
    };
  }
}
